import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay,
  QueryParam,
} from './common'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export interface ServicePointGroupFilterState {
  servicePointGroups: ServicePointGroupWithRefs[],
  config: {
    meta: {
      fieldName: string,
      fieldGroup: string,
      fieldDisplay: string,
    },
    allowMultiple?: boolean,
    constant?: boolean
  }
}

export const initialServicePointFilterState: ServicePointGroupFilterState = {
  servicePointGroups: [],
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}


export interface SetServicePointGroupFilterConfigAction {
  type: 'SET_SERVICE_POINT_GROUP_FILTER_CONFIG_ACTION',
  payload: Pick<ServicePointGroupFilterState, 'config'>,
  meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>
}
export interface AddServicePointGroupsAction {
  type: 'ADD_SERVICE_POINT_GROUPS_ACTION',
  payload: Pick<ServicePointGroupFilterState, 'servicePointGroups'>,
  meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>
}
export interface RemoveServicePointGroupsAction {
  type: 'REMOVE_SERVICE_POINT_GROUPS_ACTION',
  payload: Pick<ServicePointGroupFilterState, 'servicePointGroups'>,
  meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>
}
export interface SetServicePointGroupsAction {
  type: 'SET_SERVICE_POINT_GROUPS_ACTION',
  payload: Pick<ServicePointGroupFilterState, 'servicePointGroups'>,
  meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>
}

export type ServicePointGroupFilterAction =
  | SetServicePointGroupFilterConfigAction
  | AddServicePointGroupsAction
  | RemoveServicePointGroupsAction
  | SetServicePointGroupsAction

export const servicePointGroupFilterReducer = (state: ServicePointGroupFilterState, action: ServicePointGroupFilterAction): ServicePointGroupFilterState => {
  switch (action.type) {
    case 'SET_SERVICE_POINT_GROUP_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_SERVICE_POINT_GROUPS_ACTION':
      return {
        ...state,
        servicePointGroups: state.config.allowMultiple
          ? action.payload.servicePointGroups
          : [action.payload.servicePointGroups[0]].filter(Boolean),
      }
    case 'REMOVE_SERVICE_POINT_GROUPS_ACTION':
      return {
        ...state,
        servicePointGroups: state.servicePointGroups.filter(sspg => !action.payload.servicePointGroups.some(aspg => aspg.service_point_group_uid === sspg.service_point_group_uid)),
      }
    case 'ADD_SERVICE_POINT_GROUPS_ACTION':
      if (state.config.allowMultiple) {
        return {
          ...state,
          servicePointGroups: [
            ...state.servicePointGroups.filter(sspg => !action.payload.servicePointGroups.some(aspg => aspg.service_point_group_uid === sspg.service_point_group_uid)),
            ...action.payload.servicePointGroups.filter(aspg => !state.servicePointGroups.some(sspg => sspg.service_point_group_uid === aspg.service_point_group_uid)),
          ],
        }
      }
      return {
        ...state,
        servicePointGroups: state.servicePointGroups[0]?.service_point_group_uid !== action.payload.servicePointGroups[0]?.service_point_group_uid
          ? [action.payload.servicePointGroups[0]].filter(Boolean)
          : [],
      }
    default:
      return state
  }
}

export class ServicePointGroupFilter {
  public readonly type = 'SERVICE_POINT_GROUP'

  public static SetServicePointGroupFilterConfigAction(
    payload: SetServicePointGroupFilterConfigAction['payload'],
    meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointGroupFilterConfigAction { return { type: 'SET_SERVICE_POINT_GROUP_FILTER_CONFIG_ACTION', payload, meta } }
  public static AddServicePointGroupsAction(
    payload: AddServicePointGroupsAction['payload'],
    meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>,
  ): AddServicePointGroupsAction { return { type: 'ADD_SERVICE_POINT_GROUPS_ACTION', payload, meta } }
  public static RemoveServicePointsAction(
    payload: RemoveServicePointGroupsAction['payload'],
    meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>,
  ): RemoveServicePointGroupsAction { return { type: 'REMOVE_SERVICE_POINT_GROUPS_ACTION', payload, meta } }
  public static SetServicePointGroupAction(
    payload: SetServicePointGroupsAction['payload'],
    meta: Omit<ServicePointGroupFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetServicePointGroupsAction { return { type: 'SET_SERVICE_POINT_GROUPS_ACTION', payload, meta } }

  public state: ServicePointGroupFilterState
  constructor(
    partialStateWithConfig: Partial<ServicePointGroupFilterState> & Pick<ServicePointGroupFilterState, 'config'>,
    private dispatch: (action: ServicePointGroupFilterAction) => void,
  ) {
    this.state = { ...initialServicePointFilterState, ...partialStateWithConfig }
  }

  public apply(action: ServicePointGroupFilterAction) {
    this.state = servicePointGroupFilterReducer(this.state, action)
    return this
  }

  public asParams(): FilterParams[] {
    return this.state.servicePointGroups.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              operation: 'in=',
              value: [
                this.state.servicePointGroups.map(sp => sp.service_point_group_uid),
              ].join(','),
            },
          ],
        },
      ]
      : []
  }

  public asActiveDisplay(): ActiveFilterDisplay[] {
    return this.state.servicePointGroups.map((spg) => ({
      headerName: this.state.config.meta.fieldDisplay,
      operation: '=',
      value: spg.service_point_group_name,
      constant: this.state.config.constant ?? false,
      remove: (() =>
        this.dispatch(ServicePointGroupFilter.RemoveServicePointsAction(
          { servicePointGroups: [spg] },
          this.state.config.meta,
        ))
      ),
    }))
  }

  public asCount() {
    return this.state.servicePointGroups.length
  }

  public fromUrlQueryParams(
    params: readonly QueryParam[],
    refs: ServicePointGroupWithRefs[],
    defaultVal: ServicePointGroupWithRefs[] | string | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    let importedState: ServicePointGroupWithRefs[] = []
    if (qp?.value) {
      importedState = qp.value
        .split(',')
        .reduce((acc, str) => {
          const foundEntity = refs.find(r => r.service_point_group_uid === str)
          return foundEntity ? [...acc, foundEntity] : acc
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal === 'string') {
      importedState = defaultVal.split(',')
        .reduce((acc, str) => {
          // use name instead of uids in this case only
          const foundEntity = refs.find(r => r.service_point_group_name === str)
          return foundEntity ? [...acc, foundEntity] : acc
        },
        importedState,
        )
    }

    else if (defaultVal && typeof defaultVal !== 'string') {
      importedState = defaultVal
        .filter((dv) => refs
          .some(r => r.service_point_group_uid === dv.service_point_group_uid),
        )
    }

    this.dispatch(ServicePointGroupFilter.SetServicePointGroupAction(
      { servicePointGroups: importedState },
      this.state.config.meta,
    ))
  }

  public asUrlQueryParams(): QueryParam[] {
    return this.state.servicePointGroups.length
      ?
      [{
        name: this.state.config.meta.fieldName,
        value: this.state.servicePointGroups
          .map((sp) => sp.service_point_group_uid)
          .join(','),
      }]
      : []
  }

  public asDashboardQueryParams(): QueryParam[] {
    return this.state.servicePointGroups.length
      ?
      [{
        name: this.state.config.meta.fieldName,
        value: this.state.servicePointGroups
          .map((sp) => sp.service_point_group_name)
          .join(','),
      }]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    return {}
  }
}
